import { Control, useWatch } from 'react-hook-form'
import { 
  QuestionGroupCard, 
  generateIconInput,
  QuestionCard,
  CheckboxInput,
  Accordion,
  ActionButton,
  SimpleText,
  Image,
  CustomCheckboxInput
} from '@my/ui'
import { AccordionTrigger, DropdownField, InputField } from '../helpers/Helper'
import images from '../../../assets/images'
import { color } from '@my/util'
import { FinancialAccountsTable, FinancialAccountsTableRows } from './LinkedFinancialAccounts'
import { confirmationOptions, April2026UpdatedOptions } from '../data'

interface DebtsAndLiabilitiesProps {
  control: Control<any>
  linkedFinancialData: Record<string, any>
  goToAddAccountsTab: () => void;
}

const CashInput = (
    controllerOptions,
    inputOptions,
  ) => {
    return <InputField
              control={controllerOptions.control}
              name={controllerOptions.name}
              placeholder={inputOptions.placeholderText || inputOptions.label}
              label={inputOptions.label}
              icon={images.money}
            />
}

export const DebtsAndLiabilities: React.FC<DebtsAndLiabilitiesProps> = ({ control, linkedFinancialData, goToAddAccountsTab }) => {
  const willTakeOnMoreDebt = useWatch({ name: 'Debt - Plans - Take on more'});
  const willPayOffDebtSooner = useWatch({ name: 'Debt - Plans - Pay off sooner'});
  const allAccounts = FinancialAccountsTableRows(linkedFinancialData);
  const hasFederalStudentLoans = !!(allAccounts?.loan || []).filter(({subtype}) => subtype === 'student federal').length
  //const CashInput = generateIconInput('cash')

  return (
    <>
      <QuestionCard questionGroupTitle="Please review the balances of all your debt and liability accounts:">
        <Accordion overflow="hidden" type="multiple" defaultValue={['a1']}>
          <Accordion.Item value="a1">
            <AccordionTrigger title={'Credit Cards'} />
            <Accordion.HeightAnimator transition="medium">
              <Accordion.Content transition="medium" exitStyle={{ opacity: 0 }} style={{backgroundColor: 'white'}} paddingHorizontal={0}>
                <FinancialAccountsTable linkedFinancialData={linkedFinancialData} type={'credit'} />
              </Accordion.Content>
            </Accordion.HeightAnimator>
          </Accordion.Item>
          <Accordion.Item value="a2">
            <AccordionTrigger title={'Loans'} />
            <Accordion.HeightAnimator transition="medium">
              <Accordion.Content transition="medium" exitStyle={{ opacity: 0 }} style={{backgroundColor: 'white'}} paddingHorizontal={0}>
                <FinancialAccountsTable linkedFinancialData={linkedFinancialData} type={'loan'} />
              </Accordion.Content>
            </Accordion.HeightAnimator>
          </Accordion.Item>
        </Accordion>
        <ActionButton
          unstyled
          onPress={() => goToAddAccountsTab()}
          //disabled={!ready}
          borderWidth={1}
          borderColor={color.tzaGreen}
          hoverStyle={{backgroundColor: '#e7e7e7'}}
          borderStyle="dashed"
          height={90}
          bg={color.white}
          style={{ alignItems: 'center', justifyComment: 'center', paddingTop:10 }}
          borderRadius={10}
          gap={5}
          icon={
            <Image
              src={images.linkAccount}
              style={{ height: 35, width: 45 }}
              resizeMode="contain"
            />
          }
        >
          <SimpleText fontSize={14} fontWeight={'700'} color={color.grey}>
            Add any missing Accounts
          </SimpleText>
        </ActionButton>
        {/* {CashInput(
          { control, name: 'Debt - Balance - Credit Cards (other)' },
          { label: 'Total Balance of all Credit Cards' }
        )}
        {CashInput(
          { control, name: 'Debt - Balance - Mortgages (other)' },
          { label: 'Total Balance of all Mortgages' }
        )}
        {CashInput(
          { control, name: 'Debt - Balance - Home Equity Loans (other)' },
          { label: 'Total Balance of all Home Equity Loans' }
        )}
        {CashInput(
          { control, name: 'Debt - Balance - Student Loans (other)' },
          { label: 'Total Balance of all Student Loans' }
        )}
        {CashInput(
          { control, name: 'Debt - Balance - Auto Loans (other)' },
          { label: 'Total Balance of all Auto Loans' }
        )}
        {CashInput(
          { control, name: 'Debt - Balance - (Other loans)' },
          { label: 'Total Balance of any other loans/debts' }
        )} */}
      </QuestionCard>
      {/* <QuestionCard questionGroupTitle="Are there any plans to increase or decrease your debt?">
        {CheckboxInput(
          { control, name: 'Debt - Plans - Take on more' },
          { label: 'Do you plan to take on more debt?' }
        )}
        {CheckboxInput(
          { control, name: 'Debt - Plans - Pay off sooner' },
          { label: 'Do you plan to pay off existing debts sooner?' }
        )}
      </QuestionCard> */}
      <QuestionCard questionGroupTitle="Do you plan to take on more debt? If so, how much?">
        <CustomCheckboxInput
          options={confirmationOptions}
          control={control}
          name='Debt - Plans - Take on more' // Checkbox to indicate if an emergency fund exists
        />
        {willTakeOnMoreDebt && <InputField
          control={control}
          name='Debt - Plans - Take on how much more'
          placeholder="How much more additional debt?"
          icon={images.money}
        />}
      </QuestionCard>
      <QuestionCard questionGroupTitle="Do you plan to pay off existing debts sooner? If so, how soon?">
        <CustomCheckboxInput
          options={confirmationOptions}
          control={control}
          name='Debt - Plans - Pay off sooner' // Checkbox to indicate if an emergency fund exists
        />
        {willPayOffDebtSooner && <InputField
          control={control}
          name='Debt - Plans - Pay off how much sooner'
          placeholder="How much debt will you pay off soon?"
          icon={images.money}
        />}
      </QuestionCard>
      {hasFederalStudentLoans ? <>
        <QuestionCard questionGroupTitle="Are any of your student loans currently in deferment or forbearance?">
          <CustomCheckboxInput
            options={confirmationOptions}
            control={control}
            name='Debt - Has Loans in deferment/forbearance'
            //rules={{required: true}}
          />
        </QuestionCard>

        <QuestionCard questionGroupTitle="What repayment plan are your federal student loans on?">
          <DropdownField
            control={control}
            name="Debt - Federal Loan repayment plan(s)"
            items={April2026UpdatedOptions['Debts & Liabilities'].federalStudentLoanRepayment}
            native
          />
        </QuestionCard>
      </> : null}
        
      <QuestionCard questionGroupTitle="Do you have any high-interest debt (5%+)?">
        <CustomCheckboxInput
          options={confirmationOptions}
          control={control}
          name='Debt - Has High Interest'
          //rules={{required: true}}
        />
      </QuestionCard>
    </>
  )
}
